import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit, faSync, faWallet, faServer, faTimes, faCheck, faEye } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../AuthContext';
import Modal from '../../components/Modal';
import './AdminProviders.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

export default function AdminProviders() {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', api_url: '', api_key: '', status: 'active' });
  const [loading, setLoading] = useState(false);
  const [previewServices, setPreviewServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewProvider, setPreviewProvider] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchProviders = async () => {
    const res = await fetch(`${API}/admin/providers`, { headers });
    const data = await res.json();
    if (Array.isArray(data)) setProviders(data);
  };

  useEffect(() => { fetchProviders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', api_url: '', api_key: '', status: 'active' });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, api_url: p.api_url, api_key: p.api_key, status: p.status });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    const url = editing ? `${API}/admin/providers/${editing.id}` : `${API}/admin/providers`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
    const data = await res.json();
    if (!res.ok) { setModal({ open: true, type: 'error', title: 'Save Failed', message: data.message || 'Save failed' }); setLoading(false); return; }
    setShowModal(false);
    setLoading(false);
    fetchProviders();
  };

  const handleDelete = (id) => {
    setConfirmCb(() => async () => {
      await fetch(`${API}/admin/providers/${id}`, { method: 'DELETE', headers });
      fetchProviders();
      setModal({ open: true, type: 'success', title: 'Deleted', message: 'Provider deleted successfully.' });
    });
    setModal({ open: true, type: 'confirm', title: 'Delete Provider', message: 'Are you sure you want to delete this provider? This action cannot be undone.' });
  };

  const [modal, setModal] = useState({ open: false, type: 'info', title: '', message: '' });
  const [confirmCb, setConfirmCb] = useState(null);

  const handleBalance = async (id) => {
    const res = await fetch(`${API}/admin/providers/${id}/balance`, { method: 'POST', headers });
    const data = await res.json();
    if (data.balance !== undefined) {
      setModal({ open: true, type: 'success', title: 'Balance', message: `Balance: ${data.balance}` });
      fetchProviders();
    } else {
      setModal({ open: true, type: 'error', title: 'Error', message: data.error || 'Unknown error' });
    }
  };

  const handlePreview = async (provider) => {
    setPreviewLoading(true);
    setPreviewProvider(provider);
    setSelectedServices([]);
    const res = await fetch(`${API}/admin/providers/${provider.id}/preview-services`, { method: 'POST', headers });
    const data = await res.json();
    setPreviewLoading(false);
    if (data.error) {
      setModal({ open: true, type: 'error', title: 'Fetch Failed', message: data.error });
      return;
    }
    if (Array.isArray(data)) {
      setPreviewServices(data);
      setSelectedServices(data.filter((s) => !s.exists).map((s) => s.provider_service_id));
    }
  };

  const toggleService = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleImport = async () => {
    const toImport = previewServices.filter((s) => selectedServices.includes(s.provider_service_id));
    if (toImport.length === 0) {
      setModal({ open: true, type: 'error', title: 'No Services', message: 'Select at least one service to import.' });
      return;
    }
    setModal({ open: true, type: 'info', title: 'Importing...', message: `Importing ${toImport.length} services...` });
    const res = await fetch(`${API}/admin/providers/${previewProvider.id}/import-services`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ services: toImport }),
    });
    const data = await res.json();
    setPreviewServices([]);
    setPreviewProvider(null);
    if (data.error) {
      setModal({ open: true, type: 'error', title: 'Import Failed', message: data.error });
    } else {
      setModal({ open: true, type: 'success', title: 'Import Complete', message: data.message || 'Done' });
    }
  };

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="admin-providers">
      <div className="providers-header">
        <button className="providers-add-btn" onClick={openAdd}>
          <FontAwesomeIcon icon={faPlus} /> Add Provider
        </button>
      </div>

      <div className="providers-grid">
        {providers.map((p) => (
          <div key={p.id} className="provider-card">
            <div className="provider-card-top">
              <h3 className="provider-name">{p.name}</h3>
              <span className={`provider-status ${p.status}`}>{p.status}</span>
            </div>
            <div className="provider-meta">
              <span className="provider-url">{p.api_url}</span>
              <span className="provider-balance">
                <FontAwesomeIcon icon={faWallet} /> Balance: {p.balance ?? 'N/A'}
              </span>
            </div>
            <div className="provider-actions">
              <button className="provider-btn balance" onClick={() => handleBalance(p.id)} title="Check Balance">
                <FontAwesomeIcon icon={faWallet} /> Balance
              </button>
              <button className="provider-btn fetch" onClick={() => handlePreview(p)} title="Preview & Import Services">
                <FontAwesomeIcon icon={faEye} /> Preview
              </button>
              <button className="provider-btn edit" onClick={() => openEdit(p)} title="Edit">
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button className="provider-btn delete" onClick={() => handleDelete(p.id)} title="Delete">
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}
        {providers.length === 0 && (
          <div className="providers-empty">
            <FontAwesomeIcon icon={faServer} className="empty-icon" />
            <p>No providers configured yet.</p>
          </div>
        )}
      </div>

      {previewProvider && (
        <div className="modal-overlay" onClick={() => { setPreviewServices([]); setPreviewProvider(null); }}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Services from {previewProvider.name}</h2>
              <button className="modal-close" onClick={() => { setPreviewServices([]); setPreviewProvider(null); }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            {previewLoading ? (
              <div className="preview-loading">Loading services...</div>
            ) : (
              <>
                <div className="preview-actions">
                  <span className="preview-count">{selectedServices.length} of {previewServices.length} selected</span>
                  <label className="preview-select-all">
                    <input
                      type="checkbox"
                      checked={selectedServices.length === previewServices.length}
                      onChange={() =>
                        selectedServices.length === previewServices.length
                          ? setSelectedServices([])
                          : setSelectedServices(previewServices.map((s) => s.provider_service_id))
                      }
                    />
                    Select All
                  </label>
                </div>
                <div className="preview-list">
                  {previewServices.map((svc) => (
                    <label key={svc.provider_service_id} className={`preview-item ${svc.exists ? 'exists' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedServices.includes(svc.provider_service_id)}
                        onChange={() => toggleService(svc.provider_service_id)}
                        disabled={svc.exists}
                      />
                      <div className="preview-item-info">
                        <span className="preview-item-name">{svc.name}</span>
                        <span className="preview-item-meta">
                          {svc.category} — TSHS {Number(svc.rate).toLocaleString()}/1K — Min: {Number(svc.min).toLocaleString()} Max: {Number(svc.max).toLocaleString()}
                        </span>
                      </div>
                      {svc.exists && <span className="preview-item-badge">Exists</span>}
                    </label>
                  ))}
                </div>
                <div className="modal-footer">
                  <button className="cancel-btn" onClick={() => { setPreviewServices([]); setPreviewProvider(null); }}>Cancel</button>
                  <button className="save-btn" onClick={handleImport} disabled={selectedServices.length === 0}>
                    <FontAwesomeIcon icon={faCheck} /> Import Selected ({selectedServices.length})
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Provider' : 'Add Provider'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSave} className="modal-form">
              <div className="form-group">
                <label className="form-label">Provider Name</label>
                <input className="form-input" value={form.name} onChange={set('name')} required />
              </div>
              <div className="form-group">
                <label className="form-label">API URL</label>
                <input className="form-input" value={form.api_url} onChange={set('api_url')} placeholder="https://api.provider.com/api/v2" required />
              </div>
              <div className="form-group">
                <label className="form-label">API Key</label>
                <input className="form-input" value={form.api_key} onChange={set('api_key')} required />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={set('status')}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </form>
          </div>
        </div>
      )}

      <Modal
        open={modal.open}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => { setModal({ ...modal, open: false }); setConfirmCb(null); }}
        onConfirm={() => { setModal({ ...modal, open: false }); setTimeout(() => confirmCb?.(), 100); }}
        confirmLabel="Fetch"
      />
    </div>
  );
}
